import { Component, input, signal, computed, ElementRef, ViewChild, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-virtual-scroll',
  imports: [CommonModule],
  template: `
    <div
      #scrollContainer
      [class]="containerClasses"
      (scroll)="onScroll()"
      [style.height]="height()">
      <div [style.height.px]="totalHeight()" class="relative">
        <div 
          [style.transform]="'translateY(' + offsetY() + 'px)'"
          class="absolute top-0 left-0 right-0">
          @for (item of visibleItems(); track trackByFn($index, item)) {
            <div [style.height.px]="itemHeight()">
              <ng-container *ngTemplateOutlet="itemTemplate; context: { $implicit: item, index: startIndex() + $index }"></ng-container>
            </div>
          }
        </div>
      </div>
    </div>
    
    <ng-template #itemTemplate let-item let-index="index">
      <ng-content></ng-content>
    </ng-template>
  `,
  styles: []
})
export class VirtualScrollComponent implements AfterViewInit, OnDestroy {
  items = input<any[]>([]);
  itemHeight = input<number>(50);
  height = input<string>('400px');
  buffer = input<number>(5);

  @ViewChild('scrollContainer', { static: false }) scrollContainer!: ElementRef<HTMLElement>;

  scrollTop = signal(0);
  private scrollListener?: () => void;

  ngAfterViewInit(): void {
    if (this.scrollContainer) {
      this.scrollListener = () => this.onScroll();
      this.scrollContainer.nativeElement.addEventListener('scroll', this.scrollListener, { passive: true });
    }
  }

  ngOnDestroy(): void {
    if (this.scrollListener && this.scrollContainer) {
      this.scrollContainer.nativeElement.removeEventListener('scroll', this.scrollListener);
    }
  }

  onScroll(): void {
    if (this.scrollContainer) {
      this.scrollTop.set(this.scrollContainer.nativeElement.scrollTop);
    }
  }

  totalHeight = computed(() => {
    return this.items().length * this.itemHeight();
  });

  startIndex = computed(() => {
    const index = Math.floor(this.scrollTop() / this.itemHeight());
    return Math.max(0, index - this.buffer());
  });

  endIndex = computed(() => {
    const containerHeight = this.scrollContainer?.nativeElement?.clientHeight || 400;
    const visibleCount = Math.ceil(containerHeight / this.itemHeight());
    const index = this.startIndex() + visibleCount + this.buffer();
    return Math.min(this.items().length, index);
  });

  visibleItems = computed(() => {
    return this.items().slice(this.startIndex(), this.endIndex());
  });

  offsetY = computed(() => {
    return this.startIndex() * this.itemHeight();
  });

  trackByFn(index: number, item: any): any {
    return item?.id || index;
  }

  get containerClasses(): string {
    return 'overflow-y-auto w-full';
  }
}

